import { ASSET_MANIFEST } from './AssetManifest';
export { ASSET_MANIFEST };

// Pack-based loading system (Quickit)
export { AssetPackManager } from './AssetPackManager';
export type {
  PackIndex,
  PackEntry,
  PackManifest,
  PackAtlas,
  PackTileset,
  PackMap,
  PackAudio,
} from './AssetPackManager';

export type AssetGroup = keyof typeof ASSET_MANIFEST.mappings;

/**
 * Get the file URL for a given logical asset key.
 * 
 * @param group The asset group (e.g., 'WORLD_TILES', 'MONSTERS')
 * @param key The logical key (e.g., 'FLOOR_STONE_A')
 * @returns The relative URL to the asset file (e.g., 'assets/...') or null if not found/mapped.
 */
export function getAssetUrl(group: AssetGroup, key: string): string | null {
  // We cast to any because the key is dynamic
  const groupData = ASSET_MANIFEST.mappings[group] as Record<string, { file: string | null }>;
  if (!groupData) return null;
  
  const entry = groupData[key];
  return entry?.file || null;
}

/**
 * Get the frame index for a given logical asset key.
 * This is useful for spritesheet-based lookups.
 */
export function getAssetFrame(group: AssetGroup, key: string): number | null {
  const groupData = ASSET_MANIFEST.mappings[group] as Record<string, { frame: number | string }>;
  if (!groupData) return null;
  
  const entry = groupData[key];
  if (entry && typeof entry.frame === 'number') {
    return entry.frame;
  }
  return null;
}

// ============================================================================
// CDN Asset Loading
// ============================================================================

/**
 * The CDN URL for assets hosted on Cloudflare R2.
 * Falls back to empty string (local assets) if not configured.
 */
export const CDN_URL = import.meta.env.VITE_CDN_URL || '';

/**
 * Convert a local asset path to a CDN URL if CDN is configured.
 * 
 * @param localPath The local asset path (e.g., 'assets/nature/grass.png')
 * @returns CDN URL if available, otherwise the original local path
 * 
 * @example
 * // With CDN configured:
 * cdnAssetUrl('assets/nature/grass.png') 
 * // => 'https://gfx.badgey.org/polished/nature/grass.png'
 * 
 * // Without CDN:
 * cdnAssetUrl('assets/nature/grass.png')
 * // => 'assets/nature/grass.png'
 */
export function cdnAssetUrl(localPath: string): string {
  // If no CDN configured, return local path
  if (!CDN_URL) {
    return localPath;
  }
  
  // If path already starts with http/https, return as-is
  if (localPath.startsWith('http://') || localPath.startsWith('https://')) {
    return localPath;
  }
  
  // Convert local 'assets/...' path to CDN 'polished/...' path
  // Local: assets/nature/grass.png -> CDN: polished/nature/grass.png
  if (localPath.startsWith('assets/')) {
    const relativePath = localPath.slice('assets/'.length);
    return `${CDN_URL}/polished/${relativePath}`;
  }
  
  // For paths that don't start with 'assets/', prepend CDN + polished
  return `${CDN_URL}/polished/${localPath}`;
}
